// Iter 204g / Iter 205 — Gold Finland live exact-score + insight panel.
//
// Renders three phase-specific card styles:
//   * EARLY / LATE → exact-score / additional-goal card (gold + watch)
//   * HALFTIME     → 2H scouting insight (no FT score, next-goal owner,
//                    Finland late-15' intensity badge, SOT split)
//   * LATE         → additional-goal lock + projected FT
//
// All Finland fixtures are surfaced regardless of confidence in their
// own bucket; the GOLD ribbon is reserved for EARLY/LATE picks at
// ≥70% confidence.  Refreshes every 15 s.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Crown, Sparkles, TrendingUp, Target, Clock, Flame, Coffee, Zap,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { API } from "../App";
import { formatLiveMinute } from "../lib/liveMinute";

const POLL_MS = 15000;

const TIER_TONE = {
  "A+ GOLD":     "text-amber-300 border-amber-400/60 bg-amber-400/15",
  "A GOLD":      "text-amber-300 border-amber-500/40 bg-amber-500/10",
  "B GOLD":      "text-yellow-300 border-yellow-500/30 bg-yellow-500/5",
  "WATCH":       "text-muted-foreground border-border bg-card/40",
  "AT HALFTIME": "text-sky-300 border-sky-500/40 bg-sky-500/10",
  "PRE-GATE":    "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
};

function MatchHeader({ entry }) {
  return (
    <>
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <span className="truncate flex-1">
          {entry.league?.name || "—"} · {entry.league?.country || ""}
        </span>
        <span className="inline-flex items-center gap-1 shrink-0">
          <Clock className="w-3 h-3" />
          {entry.phase === "HALFTIME" ? "HT" : (formatLiveMinute(entry.minute, entry.minute_extra, entry.status) || `${entry.minute}'`)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
        <span className="font-semibold truncate flex-1 text-sm">
          {entry.home_team || "Home"}
        </span>
        <span className="font-bold font-display tabular-nums text-base text-foreground shrink-0 px-2 rounded bg-background/60 border border-border">
          {entry.current_score}
        </span>
        <span className="font-semibold truncate flex-1 text-right text-sm">
          {entry.away_team || "Away"}
        </span>
      </div>
    </>
  );
}

function PhaseRibbon({ entry, icon: Icon, label }) {
  const tone = TIER_TONE[entry.tier] || TIER_TONE.WATCH;
  return (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-bold text-amber-300/90">
        <Icon className="w-3 h-3" />
        {label}
      </span>
      <span
        data-testid={`gold-fin-tier-${entry.fixture_id}`}
        className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${tone}`}
      >
        {entry.tier}
      </span>
    </div>
  );
}

function EarlyOrLateCard({ entry, idx, isGold }) {
  const topScore = entry.exact_scores?.[0];
  const second = entry.exact_scores?.[1];
  const fb = entry.final_bucket;
  const isLate = entry.phase === "LATE";
  const ag = entry.additional_goal;
  return (
    <article
      data-testid={`gold-fin-row-${idx}`}
      className={`rounded-xl border bg-card/50 p-4 transition-colors ${
        isGold
          ? "border-amber-400/40 hover:border-amber-300/70 shadow-[0_0_24px_rgba(245,158,11,0.10)]"
          : "border-border hover:border-foreground/30"
      }`}
    >
      <MatchHeader entry={entry} />

      <div className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5 mb-2">
        <PhaseRibbon
          entry={entry}
          icon={isLate ? Zap : isGold ? Crown : Target}
          label={isLate ? "Late lock · Projected FT" : "Predicted FT"}
        />
        <div className="flex items-baseline justify-between gap-2">
          <span
            data-testid={`gold-fin-row-${idx}-score`}
            className="font-display font-bold text-2xl tabular-nums"
          >
            {topScore?.score || "—"}
          </span>
          <span
            data-testid={`gold-fin-row-${idx}-conf`}
            className="font-bold tabular-nums text-amber-300 text-lg shrink-0"
          >
            {entry.confidence_pct}%
          </span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">
          {topScore && (
            <>
              {topScore.probability_pct}% chance of finishing {topScore.score}
              {second && (
                <> · {second.score} @ {second.probability_pct}% (range hits {entry.score_range?.combined_probability_pct}%)</>
              )}
            </>
          )}
        </div>
      </div>

      {/* LATE-phase additional-goal lock-in strip */}
      {isLate && ag && (
        <div
          data-testid={`gold-fin-row-${idx}-late-lock`}
          className="rounded-lg border border-amber-400/30 bg-amber-500/[0.07] px-3 py-2 mb-2"
        >
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-amber-300 font-bold mb-1">
            <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3" /> Additional-goal lock</span>
            <span className="tabular-nums">{ag.probability_pct}%</span>
          </div>
          <div className="text-[10px] text-muted-foreground leading-relaxed">{ag.note}</div>
        </div>
      )}

      {/* Bucket strip */}
      {fb && (
        <div className="grid grid-cols-3 gap-1.5 text-[10px]">
          <div className="rounded border border-border bg-background/30 px-2 py-1.5">
            <div className="uppercase tracking-[0.14em] text-muted-foreground">No more</div>
            <div className="font-bold tabular-nums text-foreground">{fb.no_more_goals_pct}%</div>
          </div>
          <div className="rounded border border-border bg-background/30 px-2 py-1.5">
            <div className="uppercase tracking-[0.14em] text-muted-foreground">+1 goal</div>
            <div className="font-bold tabular-nums text-foreground">{fb.exactly_one_more_pct}%</div>
          </div>
          <div className="rounded border border-border bg-background/30 px-2 py-1.5">
            <div className="uppercase tracking-[0.14em] text-muted-foreground">+2 or more</div>
            <div className="font-bold tabular-nums text-foreground">{fb.two_plus_more_pct}%</div>
          </div>
        </div>
      )}
      {entry.next_goal_owner !== "none" && (
        <div className="mt-2 text-[10px] text-muted-foreground inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-amber-300/80" />
          Next-goal pressure favours {entry.next_goal_owner === "home" ? entry.home_team : entry.away_team}
        </div>
      )}
      {entry.intensity_multiplier > 1.0 && (
        <div className="mt-1 text-[10px] text-amber-300/70 inline-flex items-center gap-1">
          <Flame className="w-3 h-3" />
          {entry.country === "sweden" ? "Swedish" : entry.country === "norway" ? "Norwegian" : "Finnish"} late-15&apos; intensity ×{entry.intensity_multiplier}
        </div>
      )}
    </article>
  );
}

function HalftimeCard({ entry, idx }) {
  const insight = entry.halftime_insight || {};
  const sot = insight.shots_on_target || { home: 0, away: 0 };
  const da = insight.dangerous_attacks || { home: 0, away: 0 };
  const homeShare = insight.next_goal_share_home_pct ?? 50;
  const awayShare = insight.next_goal_share_away_pct ?? 50;
  return (
    <article
      data-testid={`gold-fin-ht-${idx}`}
      className="rounded-xl border border-sky-500/30 bg-card/50 p-4 hover:border-sky-400/70 transition-colors shadow-[0_0_24px_rgba(56,189,248,0.08)]"
    >
      <MatchHeader entry={entry} />

      <div className="rounded-lg border border-sky-500/30 bg-sky-500/[0.06] px-3 py-2.5 mb-2">
        <PhaseRibbon entry={entry} icon={Coffee} label="Halftime · 2H scouting" />
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-display font-bold text-base text-sky-200">
            No FT call yet
          </span>
          <span className="font-bold tabular-nums text-sky-300 text-sm">
            Expected 2H goals {insight.expected_2h_goals_total}
          </span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
          {insight.note}
        </div>
      </div>

      {/* Next-goal owner share bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
          <span>Next-goal share</span>
          <span className="tabular-nums">{entry.confidence_pct}% conf</span>
        </div>
        <div className="flex h-2 rounded overflow-hidden border border-border bg-background/40">
          <div
            className="bg-sky-400/70"
            style={{ width: `${homeShare}%` }}
            data-testid={`gold-fin-ht-${idx}-home-share`}
            title={`${entry.home_team} ${homeShare}%`}
          />
          <div
            className="bg-rose-400/70"
            style={{ width: `${awayShare}%` }}
            data-testid={`gold-fin-ht-${idx}-away-share`}
            title={`${entry.away_team} ${awayShare}%`}
          />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5 tabular-nums">
          <span>{homeShare}%</span>
          <span>{awayShare}%</span>
        </div>
      </div>

      {/* SOT/DA split */}
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        <div className="rounded border border-border bg-background/30 px-2 py-1.5">
          <div className="uppercase tracking-[0.14em] text-muted-foreground">SOT</div>
          <div className="font-bold tabular-nums text-foreground">{sot.home} – {sot.away}</div>
        </div>
        <div className="rounded border border-border bg-background/30 px-2 py-1.5">
          <div className="uppercase tracking-[0.14em] text-muted-foreground">Dangerous attacks</div>
          <div className="font-bold tabular-nums text-foreground">{da.home} – {da.away}</div>
        </div>
      </div>

      {entry.intensity_multiplier > 1.0 && (
        <div className="mt-2 text-[10px] text-amber-300/70 inline-flex items-center gap-1">
          <Flame className="w-3 h-3" />
          {entry.country === "sweden" ? "Swedish" : entry.country === "norway" ? "Norwegian" : "Finnish"} late-15&apos; intensity ×{entry.intensity_multiplier} priced in
        </div>
      )}
    </article>
  );
}

function PregateCard({ entry, idx }) {
  const insight = entry.pregate_insight || {};
  const sot = insight.shots_on_target || { home: 0, away: 0 };
  const da = insight.dangerous_attacks || { home: 0, away: 0 };
  return (
    <article
      data-testid={`gold-fin-pg-${idx}`}
      className="rounded-xl border border-emerald-500/30 bg-card/50 p-4 hover:border-emerald-400/70 transition-colors"
    >
      <MatchHeader entry={entry} />
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5 mb-2">
        <PhaseRibbon entry={entry} icon={Clock} label="Signal stabilising · pre-15'" />
        <div className="text-sm font-semibold text-emerald-200">
          Tracking from kickoff — engine activates at minute 15
        </div>
        <div className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
          {insight.note}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        <div className="rounded border border-border bg-background/30 px-2 py-1.5">
          <div className="uppercase tracking-[0.14em] text-muted-foreground">SOT</div>
          <div className="font-bold tabular-nums text-foreground">{sot.home} – {sot.away}</div>
        </div>
        <div className="rounded border border-border bg-background/30 px-2 py-1.5">
          <div className="uppercase tracking-[0.14em] text-muted-foreground">Dangerous attacks</div>
          <div className="font-bold tabular-nums text-foreground">{da.home} – {da.away}</div>
        </div>
      </div>
      {entry.intensity_multiplier > 1.0 && (
        <div className="mt-2 text-[10px] text-amber-300/70 inline-flex items-center gap-1">
          <Flame className="w-3 h-3" />
          {entry.country === "sweden" ? "Swedish" : entry.country === "norway" ? "Norwegian" : "Finnish"} late-15&apos; intensity ×{entry.intensity_multiplier} priced in
        </div>
      )}
    </article>
  );
}

function PickCard({ entry, idx, isGold }) {
  if (entry.phase === "PRE_GATE") {
    return <PregateCard entry={entry} idx={idx} />;
  }
  if (entry.phase === "HALFTIME") {
    return <HalftimeCard entry={entry} idx={idx} />;
  }
  return <EarlyOrLateCard entry={entry} idx={idx} isGold={isGold} />;
}


// Country-specific labels.  Each entry is everything the panel needs
// to render itself: the API endpoint, the hero title, copy, and the
// empty-state message.  Adding a new country is just a new entry.
const COUNTRY_CONFIG = {
  finland: {
    flag: "🇫🇮",
    endpoint: "gold-finland",
    eyebrow: "Gold Finland · Live exact-score AI",
    title: "Finnish leagues · late-game value live",
    description: (
      <>Every live fixture from any Finland competition (Veikkausliiga · Ykkönen · Kakkonen · Kolmonen · Suomen Cup · Naisten Liiga · lower divisions) is graded across three phases: <span className="text-foreground">Halftime</span> shows the 2H scouting + next-goal split, <span className="text-foreground">Live</span> shows the early FT read, and from minute 75 the <span className="text-amber-300">Late-game lock</span> publishes the additional-goal call boosted by Finland&apos;s late-15&apos; intensity reputation.</>
    ),
    emptyMsg: "No Finnish league fixtures live right now.  Check back when a Veikkausliiga, Ykkönen, Kakkonen, Kolmonen or cup match is in play — the engine activates from kickoff with a halftime scouting card, then locks in from minute 75.",
  },
  sweden: {
    flag: "🇸🇪",
    endpoint: "gold-sweden",
    eyebrow: "Gold Sweden · Live exact-score AI",
    title: "Swedish leagues · late-game value live",
    description: (
      <>Every live fixture from any Sweden competition (Allsvenskan · Superettan · Ettan · Damallsvenskan · Elitettan · Svenska Cupen · lower divisions) graded across three phases: <span className="text-foreground">Halftime</span> 2H scouting + next-goal split, <span className="text-foreground">Live</span> early FT read, and from minute 75 the <span className="text-amber-300">Late-game lock</span> with Nordic late-15&apos; intensity boost.</>
    ),
    emptyMsg: "No Swedish league fixtures live right now.  Check back when an Allsvenskan, Superettan, Ettan or cup match is in play.",
  },
  norway: {
    flag: "🇳🇴",
    endpoint: "gold-norway",
    eyebrow: "Gold Norway · Live exact-score AI",
    title: "Norwegian leagues · late-game value live",
    description: (
      <>Every live fixture from any Norway competition (Eliteserien · OBOS-ligaen · PostNord-ligaen · Toppserien · NM Cupen · lower divisions) graded across three phases: <span className="text-foreground">Halftime</span> 2H scouting + next-goal split, <span className="text-foreground">Live</span> early FT read, and from minute 75 the <span className="text-amber-300">Late-game lock</span> with Nordic late-15&apos; intensity boost.</>
    ),
    emptyMsg: "No Norwegian league fixtures live right now.  Check back when an Eliteserien, OBOS-ligaen, Toppserien or NM Cupen match is in play.",
  },
};

export default function GoldFinlandPanel({ country = "finland" } = {}) {
  const config = COUNTRY_CONFIG[country] || COUNTRY_CONFIG.finland;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Iter 239 — collapsible hero (default collapsed so the page stays
  // compact and three panels fit above the fold).  Persisted per-country
  // in localStorage so the user's choice survives page reloads.
  const STORAGE_KEY = `gold-${country}-hero-expanded`;
  const [heroExpanded, setHeroExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "true";
    } catch { return false; }
  });
  const toggleHero = () => {
    setHeroExpanded((v) => {
      try { localStorage.setItem(STORAGE_KEY, String(!v)); } catch { /* ignore */ }
      return !v;
    });
  };

  useEffect(() => {
    let cancel = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/live/next-goal/${config.endpoint}`);
        if (!cancel) { setData(r.data); setErr(null); }
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || e.message || "Failed");
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => { cancel = true; clearInterval(t); };
  }, [config.endpoint]);

  const gold = data?.gold || [];
  const watch = data?.watch || [];
  const halftime = data?.halftime || [];
  const pregate = data?.pregate || [];
  const totalCards = gold.length + watch.length + halftime.length + pregate.length;

  return (
    <section
      className="space-y-5"
      data-testid={`gold-${country}-panel`}
    >
      {/* Hero — collapsible (Iter 239) */}
      <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-background to-background">
        <button
          type="button"
          onClick={toggleHero}
          className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-white/[0.02] transition rounded-2xl"
          data-testid={`gold-${country}-hero-toggle`}
          aria-expanded={heroExpanded}
        >
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300">
              <Sparkles className="w-3 h-3" />
              <span className="text-base leading-none">{config.flag}</span>
              {config.eyebrow}
            </span>
            {/* Compact one-line summary when collapsed */}
            {!heroExpanded && (
              <div className="text-xs text-muted-foreground mt-1 font-mono">
                {gold.length} GOLD · {halftime.length} HALFTIME · {pregate.length} PRE-GATE · {watch.length} WATCH
                {data?.fetched_at && (
                  <> · {new Date(data.fetched_at).toLocaleTimeString()}</>
                )}
              </div>
            )}
          </div>
          {heroExpanded
            ? <ChevronUp   className="w-5 h-5 text-amber-300/70 shrink-0" />
            : <ChevronDown className="w-5 h-5 text-amber-300/70 shrink-0" />}
        </button>

        {heroExpanded && (
          <div className="px-4 sm:px-5 pb-5">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" data-testid={`gold-${country}-title`}>
              {config.title}
            </h2>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-2xl">
              {config.description}
            </p>
            <div
              data-testid={`gold-${country}-meta`}
              className="text-[10px] text-muted-foreground mt-4 font-mono"
            >
              {gold.length} GOLD · {halftime.length} HALFTIME · {pregate.length} PRE-GATE · {watch.length} WATCH · auto-refresh every {POLL_MS / 1000}s
              {data?.fetched_at && <> · last fetch {new Date(data.fetched_at).toLocaleTimeString()}</>}
            </div>
          </div>
        )}
      </div>

      {/* Body — only renders when hero is expanded (Iter 239b) */}
      {heroExpanded && (err ? (
        <div
          data-testid={`gold-${country}-error`}
          className="rounded-xl border border-rose-500/40 bg-rose-500/5 text-rose-300 text-sm p-4"
        >
          {err}
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`gold-${country}-loading`}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-44 rounded-xl border border-border bg-card/30 animate-pulse" />
          ))}
        </div>
      ) : totalCards === 0 ? (
        <div
          data-testid={`gold-${country}-empty`}
          className="rounded-xl border border-dashed border-border bg-card/20 p-10 text-center"
        >
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-amber-300/40" />
          <p className="text-sm text-muted-foreground">
            {config.emptyMsg}
          </p>
        </div>
      ) : (
        <>
          {gold.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-[0.18em] text-amber-300 mb-3 flex items-center gap-1.5">
                <Crown className="w-4 h-4" /> Gold picks · ≥{data?.min_confidence_floor || 70}% confidence
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`gold-${country}-gold-list`}>
                {gold.map((entry, idx) => (
                  <PickCard key={entry.fixture_id} entry={entry} idx={`${country}-${idx}`} isGold />
                ))}
              </div>
            </div>
          )}
          {halftime.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-[0.18em] text-sky-300 mb-3 flex items-center gap-1.5">
                <Coffee className="w-4 h-4" /> Halftime · 2H scouting · {halftime.length}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`gold-${country}-halftime-list`}>
                {halftime.map((entry, idx) => (
                  <PickCard key={entry.fixture_id} entry={entry} idx={`${country}-ht${idx}`} isGold={false} />
                ))}
              </div>
            </div>
          )}
          {pregate.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-[0.18em] text-emerald-300 mb-3 flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> Signal stabilising · pre-15&apos; · {pregate.length}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`gold-${country}-pregate-list`}>
                {pregate.map((entry, idx) => (
                  <PickCard key={entry.fixture_id} entry={entry} idx={`${country}-pg${idx}`} isGold={false} />
                ))}
              </div>
            </div>
          )}
          {watch.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-1.5">
                <Target className="w-4 h-4" /> Watch list · below gold floor
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`gold-${country}-watch-list`}>
                {watch.map((entry, idx) => (
                  <PickCard key={entry.fixture_id} entry={entry} idx={`${country}-w${idx}`} isGold={false} />
                ))}
              </div>
            </div>
          )}
        </>
      ))}
    </section>
  );
}
